/**
 * productSubdomains.js — product host and path redirects from the Product Version Registry.
 * Shaped by: VR-3; decisions D-VR3-01, D-VR3-02, D-VR3-03, D-VR3-05.
 * Invariants: a redirect URL is built only from proposedSubdomain in the registry.
 *   subdomainLive false matches no public host. The dev override is localhost-only and never production.
 * Related: productRegistry.json, ProductHostEntry.jsx, fusionMsiMarketingRouting.js.
 */
import registry from "./productRegistry.json";

function hostOnly(hostname) {
  return String(hostname || "").toLowerCase().split(":")[0];
}

function pathOnly(pathname) {
  const raw = String(pathname || "").split("?")[0].split("#")[0];
  if (raw.length > 1 && raw.endsWith("/")) return raw.slice(0, -1);
  return raw || "/";
}

export function isDevProductHostOverrideAllowed() {
  return process.env.NODE_ENV !== "production";
}

export function getProductForHost(hostname, search = "") {
  const host = hostOnly(hostname);
  const products = registry.products || [];
  const exact = products.find(
    (product) =>
      product.subdomainLive === true &&
      String(product.proposedSubdomain || "").toLowerCase() === host,
  );
  if (exact) return exact;
  if (!isDevProductHostOverrideAllowed()) return null;
  if (host !== "localhost" && host !== "127.0.0.1") return null;
  const query = String(search || "").replace(/^\?/, "");
  const key = new URLSearchParams(query).get("__productHost");
  if (!key) return null;
  return products.find((product) => product.productKey === key) || null;
}

export function getProductEntry(product) {
  if (!product || !product.subdomainTarget) return null;
  if (product.subdomainTarget.entryPath) return product.subdomainTarget.entryPath;
  const variant = (product.variants || []).find(
    (row) => row.appId === product.subdomainTarget.appId,
  );
  return variant ? variant.route : null;
}

export function getSubdomainUrlForPath(pathname) {
  const normalized = pathOnly(pathname);
  if (!normalized.startsWith("/") || normalized.slice(1).includes("/")) return null;
  const key = normalized.slice(1);
  if (!key) return null;
  const product = (registry.products || []).find((row) => row.productKey === key);
  if (!product || product.subdomainLive !== true) return null;
  return `https://${product.proposedSubdomain}/`;
}

export function liveProductPathRedirects() {
  return (registry.products || [])
    .filter((product) => product.subdomainLive === true)
    .map((product) => ({
      path: `/${product.productKey}`,
      url: `https://${product.proposedSubdomain}/`,
    }));
}
